import React, { useEffect, useState } from 'react';
import { Form, Select, Input, Button, Alert, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import CBInputNumber from '../../../components/CBInputNumber';
import { toast } from 'react-toastify';
import { passerCommande, listerOrganismesCompagnies, getParametresTarifaires, listerLotsDisponibles } from '../../../services/attestationsService';
import CBModal from '../../../components/CBModal';
import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';
const { Option } = Select;

const BENEFICIAIRE_DEFAUT = 'Bureau National Togolais de la Carte Brune';
const QUANTITE_CARTON = 10000;
const SEUIL_STOCK_FAIBLE = 2 * QUANTITE_CARTON;

export default function NouvelleCommandeModal({ open, onClose, onCreated }) {
  const { t } = useTranslation('attestations');
  const [form] = Form.useForm();
  const [organismes, setOrganismes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [qte, setQte] = useState(0);
  const [tarif, setTarif] = useState({ prixUnitaireAttestation: null, contributionFonds: null });
  const [tarifLoading, setTarifLoading] = useState(true);
  const [lotsDispo, setLotsDispo] = useState([]);
  const [stockLoading, setStockLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    listerOrganismesCompagnies().then(setOrganismes).catch(() => setOrganismes([]));
    setTarifLoading(true);
    getParametresTarifaires()
      .then(setTarif)
      .catch(() => setTarif({ prixUnitaireAttestation: null, contributionFonds: null }))
      .finally(() => setTarifLoading(false));
    setStockLoading(true);
    listerLotsDisponibles()
      .then(setLotsDispo)
      .catch(() => setLotsDispo([]))
      .finally(() => setStockLoading(false));
  }, [open]);

  const stockTotal = lotsDispo.reduce((s, l) => s + (Number(l.quantiteRestante) || 0), 0);
  const aucunLot = !stockLoading && lotsDispo.length === 0;
  const stockFaible = !stockLoading && stockTotal > 0 && stockTotal < SEUIL_STOCK_FAIBLE;
  const stockInsuffisant = !stockLoading && qte > 0 && qte > stockTotal;

  const tarifConfigure = tarif.prixUnitaireAttestation != null && tarif.contributionFonds != null;
  const totalAtt = tarifConfigure ? qte * tarif.prixUnitaireAttestation : 0;
  const totalFonds = tarifConfigure ? qte * tarif.contributionFonds : 0;
  const totalGeneral = totalAtt + totalFonds;
  const fmt = (n) => new Intl.NumberFormat(localeIntl()).format(n) + ' FCFA';

  const submitCommande = async (vals) => {
    setSubmitting(true);
    try {
      const cmd = await passerCommande({
        organismeTrackingId: vals.organismeTrackingId,
        quantite: vals.quantite,
        nomBeneficiaireCheque: vals.nomBeneficiaireCheque,
      });
      toast.success(t('modales.commande.creee', { numero: cmd?.numeroCommande }));
      form.resetFields();
      setQte(0);
      onCreated?.(cmd);
      onClose?.();
    } catch (e) {
      // l'intercepteur axios affiche déjà le message backend
    } finally {
      setSubmitting(false);
    }
  };

  const onFinish = async (vals) => {
    if (Number(vals.quantite) < QUANTITE_CARTON) {
      Modal.confirm({
        title: t('modales.commande.sousCarton'),
        icon: <ExclamationCircleOutlined style={{ color: '#f97316' }} />,
        content: (
          <span>
            <Trans
              t={t}
              i18nKey="modales.commande.sousCartonConfirmation"
              values={{ carton: QUANTITE_CARTON.toLocaleString(localeIntl()), saisie: Number(vals.quantite).toLocaleString(localeIntl()) }}
              components={{ 1: <strong />, br: <br /> }}
            />
          </span>
        ),
        okText: t('modales.commande.ouiConfirmer'),
        cancelText: t('modales.commande.modifier'),
        okButtonProps: { style: { background: '#f97316', borderColor: '#f97316' } },
        onOk: () => submitCommande(vals),
      });
      return;
    }
    submitCommande(vals);
  };

  return (
    <CBModal title={t('modales.commande.titre')}
      open={open}
      onCancel={() => { form.resetFields(); setQte(0); onClose?.(); }}
      footer={null}
      width={560}
      destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish}
        initialValues={{ nomBeneficiaireCheque: BENEFICIAIRE_DEFAUT }}
        onValuesChange={(_, all) => setQte(Number(all.quantite) || 0)}>

        {aucunLot && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }}
            message={t('modales.commande.aucunLot')} />
        )}

        {!aucunLot && stockFaible && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message={t('modales.commande.stockFaible')}
            description={t('modales.commande.stockFaibleDescription', { n: stockTotal.toLocaleString(localeIntl()) })} />
        )}

        {!aucunLot && stockInsuffisant && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }}
            message={t('modales.commande.stockInsuffisant')}
            description={t('modales.commande.stockInsuffisantDescription', { qte: qte.toLocaleString(localeIntl()), stock: stockTotal.toLocaleString(localeIntl()) })} />
        )}

        <Form.Item name="organismeTrackingId" label={t('modales.commande.compagnie')} rules={[{ required: true, message: t('modales.commande.compagnieRequise') }]}>
          <Select showSearch placeholder={t('modales.commande.selectionnerCompagnie')}
            filterOption={(input, opt) => String(opt.children).toLowerCase().includes(input.toLowerCase())}>
            {organismes.map(o => (
              <Option key={o.organismeTrackingId} value={o.organismeTrackingId}>
                {o.raisonSociale}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="quantite" label={t('modales.commande.quantite')}
          extra={t('modales.commande.quantiteAide')}
          rules={[{ required: true, message: t('modales.commande.quantiteRequise') }]}>
          <CBInputNumber min={1} max={100000} step={QUANTITE_CARTON} style={{ width: '100%' }} placeholder={t('modales.commande.quantitePlaceholder')} />
        </Form.Item>
        {qte > 0 && qte < QUANTITE_CARTON && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message={t('modales.commande.sousCarton')}
            description={t('modales.commande.sousCartonDescription', { carton: QUANTITE_CARTON.toLocaleString(localeIntl()), saisie: qte.toLocaleString(localeIntl()) })}
          />
        )}
        <Form.Item name="nomBeneficiaireCheque" label={t('modales.commande.beneficiaire')}>
          <Input placeholder={t('modales.commande.beneficiairePlaceholder')} />
        </Form.Item>

        {!tarifLoading && !tarifConfigure && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }}
            message={t('modales.commande.tarifNonConfigure')} />
        )}

        {tarifConfigure && (
          <div style={{ background: '#f0faf4', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
            <div style={{ color: '#374151', marginBottom: 6 }}>
              <Trans
                t={t}
                i18nKey="modales.commande.tarif"
                values={{ prix: fmt(tarif.prixUnitaireAttestation), fonds: fmt(tarif.contributionFonds) }}
                components={{ 1: <strong /> }}
              />
            </div>
            {qte > 0 && (
              <div style={{ borderTop: '1px dashed #c8e6d3', paddingTop: 8, marginTop: 8, color: '#1a6b3c' }}>
                <div><Trans t={t} i18nKey="modales.commande.sousTotal" values={{ montant: fmt(totalAtt) }} components={{ 1: <strong /> }} /></div>
                <div><Trans t={t} i18nKey="modales.commande.contributionFonds" values={{ montant: fmt(totalFonds) }} components={{ 1: <strong /> }} /></div>
                <div style={{ fontSize: 15, marginTop: 4 }}><Trans t={t} i18nKey="modales.commande.totalAFacturer" values={{ montant: fmt(totalGeneral) }} components={{ 1: <strong /> }} /></div>
              </div>
            )}
          </div>
        )}

        {/* Collée au bas du corps défilant : le récapitulatif tarifaire allonge
            ce formulaire, et l'action principale ne doit pas descendre avec lui
            - c'est elle qu'on cherche après avoir lu le total. */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          borderTop: '1px solid #e5e7eb', paddingTop: 16,
          position: 'sticky', bottom: 0, background: '#fff', marginTop: 8,
        }}>
          <Button onClick={() => { form.resetFields(); setQte(0); onClose?.(); }}>{t('commun.annuler')}</Button>
          <Button type="primary" htmlType="submit" loading={submitting}
            disabled={!tarifConfigure || aucunLot || stockInsuffisant}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('modales.commande.creer')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}
